/**
 * Sentry do navegador.
 *
 * A mesma regra do servidor, escrita de novo aqui porque o pacote da API não
 * entra no bundle: nada de corpo de pedido, nada de cookie, nada de e-mail, e
 * máscara de CPF, CNPJ, telefone e e-mail no que sobra.
 *
 * As trilhas automáticas de DOM e de console ficam desligadas. É a decisão que
 * mais importa nesta tela: com elas ligadas, cada tecla digitada numa nota e
 * cada `console.log` de depuração viraria metadado de erro enviado para fora.
 *
 * Texto livre comprido é tratado como clínico e não sai.
 */
import * as Sentry from '@sentry/react'

const RULES: ReadonlyArray<{ pattern: RegExp; label: string }> = [
  { pattern: /[\w.+-]+@[\w-]+\.[\w.-]+/g, label: '[email]' },
  { pattern: /\b\d{2}\.?\d{3}\.?\d{3}\/?\d{4}-?\d{2}\b/g, label: '[cnpj]' },
  { pattern: /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, label: '[cpf]' },
  { pattern: /\b(?:\+?55\s?)?\(?\d{2}\)?\s?9?\d{4}-?\d{4}\b/g, label: '[telefone]' },
]

/** Acima disso, uma string de metadado é tratada como texto livre. */
export const MAX_FREE_TEXT = 160
const REDACTED = '[texto removido]'

/** Troca e-mail, CPF, CNPJ e telefone por rótulo. Ordem importa: CNPJ antes de CPF. */
export function scrubText(text: string): string {
  let out = text
  for (const rule of RULES) out = out.replace(rule.pattern, rule.label)
  return out
}

export function scrubMetadata(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') {
    return value.length > MAX_FREE_TEXT ? REDACTED : scrubText(value)
  }
  if (depth > 6) return '[profundo]'
  if (Array.isArray(value)) return value.map((item) => scrubMetadata(item, depth + 1))
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, item] of Object.entries(value)) out[key] = scrubMetadata(item, depth + 1)
    return out
  }
  return value
}

type SentryEvent = Parameters<NonNullable<Sentry.BrowserOptions['beforeSend']>>[0]

/** A regra de privacidade do evento. Exportada porque é ela que o teste cobra. */
export function beforeSend(event: SentryEvent): SentryEvent {
  if (event.request !== undefined) {
    const { data: _data, cookies: _cookies, ...rest } = event.request
    event.request = scrubMetadata(rest) as typeof event.request
    if (event.request.headers !== undefined) {
      const { cookie: _cookie, Cookie: _Cookie, ...headers } = event.request.headers
      event.request.headers = headers
    }
  }

  if (event.user !== undefined) {
    const { email: _email, ip_address: _ip, ...user } = event.user
    event.user = scrubMetadata(user) as typeof event.user
  }

  if (event.message !== undefined) event.message = scrubText(event.message)

  if (event.exception?.values !== undefined) {
    event.exception.values = event.exception.values.map((value) => ({
      ...value,
      ...(value.value === undefined ? {} : { value: scrubText(value.value) }),
    }))
  }

  if (event.extra !== undefined) {
    event.extra = scrubMetadata(event.extra) as typeof event.extra
  }

  if (event.breadcrumbs !== undefined) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => ({
      ...crumb,
      ...(crumb.message === undefined ? {} : { message: scrubText(crumb.message) }),
      ...(crumb.data === undefined
        ? {}
        : { data: scrubMetadata(crumb.data) as Record<string, unknown> }),
    }))
  }

  return event
}

/** O que o bundle lê do ambiente. Tipado à mão: `import.meta.env` é solto. */
interface SentryEnv {
  VITE_SENTRY_DSN?: string
  VITE_SENTRY_ENVIRONMENT?: string
}

/** Sobe o Sentry quando há DSN. Sem `VITE_SENTRY_DSN` não faz nada. */
export function initSentry(): boolean {
  const env = import.meta.env as unknown as SentryEnv
  const dsn = env.VITE_SENTRY_DSN
  if (typeof dsn !== 'string' || dsn.trim() === '') return false

  Sentry.init({
    dsn,
    ...(env.VITE_SENTRY_ENVIRONMENT === undefined
      ? {}
      : { environment: env.VITE_SENTRY_ENVIRONMENT }),
    sendDefaultPii: false,
    // Só o que o próprio código reporta. Nada de rastrear navegação nem
    // desempenho: a sala não manda telemetria de sessão para lugar nenhum.
    tracesSampleRate: 0,
    integrations: (defaults) => [
      ...defaults.filter((integration) => integration.name !== 'Breadcrumbs'),
      // Trilha de DOM e de console desligadas: com elas, cada tecla digitada
      // numa nota e cada log de depuração viraria metadado enviado para fora.
      Sentry.breadcrumbsIntegration({ dom: false, console: false }),
    ],
    beforeSend,
  })
  return true
}
